import { container, SERVICE_IDENTIFIERS } from './ServiceContainer';
import { ErrorHandlerService } from '@core/exceptions/ErrorHandlerService.ts';
import { AnalyticsService } from '@core/analytics/AnalyticsService';
import { NotificationService } from '@core/notifications/NotificationService';
import { IErrorHandler, IAnalyticsService, INotificationService } from '@core/interfaces';

/**
 * Register all application services with the DI container
 * This should be called once at application startup (in index.tsx)
 */
export function registerServices(): void {
    console.log('🔷 Registering services...');

    // Register Analytics Service (AppInsights implementation)
    container.register<IAnalyticsService>(
        SERVICE_IDENTIFIERS.ANALYTICS,
        () => {
            console.log('🔷 Creating AppInsightsService instance');
            const analyticsService = new AnalyticsService();
            analyticsService.initialize();
            return analyticsService;
        },
        true // singleton
    );

    // Register Notification Service
    container.register<INotificationService>(
        SERVICE_IDENTIFIERS.NOTIFICATION,
        () => {
            console.log('🔷 Creating NotificationService instance');
            return NotificationService.getInstance();
        },
        true // singleton
    );

    // Register Error Handler Service (with dependencies)
    container.register<IErrorHandler>(
        SERVICE_IDENTIFIERS.ERROR_HANDLER,
        () => {
            console.log('🔷 Creating ErrorHandlerService instance');

            // Resolve dependencies from container (not from context)
            const analyticsService = container.resolve<IAnalyticsService>(SERVICE_IDENTIFIERS.ANALYTICS);
            const notificationService = container.resolve<INotificationService>(SERVICE_IDENTIFIERS.NOTIFICATION);

            // Create ErrorHandler with injected dependencies
            const errorHandler = new ErrorHandlerService(analyticsService, notificationService);

            // Initialize global error handlers
            errorHandler.initialize();

            return errorHandler;
        },
        true // singleton
    );

    console.log('✅ All services registered');
    console.log('Registered services:', container.getRegisteredServices());
}

/**
 * Clear all registered services
 * Useful for testing or hot module reloading
 */
export function clearServices(): void {
    console.log('🔷 registerServices: Clearing all services');
    container.clear();
}

/**
 * Check if services are registered
 */
export function areServicesRegistered(): boolean {
    return (
        container.has(SERVICE_IDENTIFIERS.ANALYTICS) &&
        container.has(SERVICE_IDENTIFIERS.NOTIFICATION) &&
        container.has(SERVICE_IDENTIFIERS.ERROR_HANDLER)
    );
}
